import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAlert } from '../../contexts/AlertContext';
import Button from '../ui/Button';
import { AWS_CONFIG } from "../../config/aws-config";

const CompanyForm: React.FC = () => {
    const [companyName, setCompanyName] = useState('');
    const [abn, setAbn] = useState('');
    const [primaryContactName, setPrimaryContactName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [showGoBackButton, setShowGoBackButton] = useState(false);
    const [loadingCompany, setLoadingCompany] = useState(true);
    const [phoneNumberError, setPhoneNumberError] = useState('');
    const { showAlert } = useAlert();
    const navigate = useNavigate();
    const location = useLocation();
    const fromTokenValidation = (location.state as { from?: string } | null)?.from === 'tokenValidation';
    const backButtonLabel = fromTokenValidation ? 'Continue →' : '← Go Back';
    const backButtonDestination = fromTokenValidation ? '/dashboard' : '/company-menu';

    const baseURL = AWS_CONFIG.apiBaseUrl;

    useEffect(() => {
        const loadCompanyData = async () => {
            const customerCode = sessionStorage.getItem('customerCode');
            const companyId = sessionStorage.getItem('companyId');
            const idToken = sessionStorage.getItem('idToken');

            if (!companyId || !idToken) {
                setLoadingCompany(false);
                return;
            }

            try {
                const response = await fetch(`${baseURL}/webV2/visitorportal/company/${companyId}?customer_code=${customerCode}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': idToken,
                        'Content-Type': 'application/json',
                    }
                });

                if (response.ok) {
                    const result = await response.json();
                    const companyData = result.returnData || result;

                    setCompanyName(companyData.company_name || '');
                    setAbn(companyData.abn || '');
                    setPrimaryContactName(companyData.primary_contact || '');
                    setEmail(companyData.email || '');
                    setPhoneNumber(companyData.phone_number || '');

                    // Show GoBack button only if companyName AND primaryContactName AND email are already populated
                    if (companyData.company_name && companyData.primary_contact && companyData.email) {
                        setShowGoBackButton(true);
                    }
                }
            } catch (error) {
                console.error('Error loading company data:', error);
            } finally {
                setLoadingCompany(false);
            }
        };

        loadCompanyData();
    }, []);

    const validateABN = (abn: string): boolean => {
        // Remove spaces and validate ABN format (11 digits)
        const abnDigits = abn.replace(/\s/g, '');
        return /^\d{11}$/.test(abnDigits);
    };

    const validatePhoneNumber = (phone: string): boolean => {
        if (!phone) return true; // Phone is optional

        // Normalise: strip spaces, dashes, parentheses
        const normalised = phone.replace(/[\s\-().]/g, '');

        // Mobile – national: 04xxxxxxxx or 05xxxxxxxx (10 digits)
        const mobileNational = /^0[45][0-9]{8}$/;

        // Mobile – international: +614xxxxxxxx or +615xxxxxxxx (12 chars after +)
        const mobileInternational = /^\+61[45][0-9]{8}$/;

        // Landline – national: 0[2378]xxxxxxxx (10 digits, valid area codes only)
        const landlineNational = /^0[2378][0-9]{8}$/;

        // Landline – international: +61[2378]xxxxxxxx
        const landlineInternational = /^\+61[2378][0-9]{8}$/;

        // Non-geographic: 1300xxxxxx or 1800xxxxxx (10 digits)
        const nonGeographic = /^1[38]00[0-9]{6}$/;

        return (
            mobileNational.test(normalised) ||
            mobileInternational.test(normalised) ||
            landlineNational.test(normalised) ||
            landlineInternational.test(normalised) ||
            nonGeographic.test(normalised)
        );
    };

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!companyName.trim()) {
            showAlert('Please enter a company name', 'error');
            return;
        }

        if (abn && !validateABN(abn)) {
            showAlert('Please enter a valid 11-digit ABN', 'error');
            return;
        }

        if (!primaryContactName.trim()) {
            showAlert('Please enter the primary contact name', 'error');
            return;
        }

        if (!email.trim() || !validateEmail(email)) {
            showAlert('Please enter a valid email address', 'error');
            return;
        }

        if (phoneNumber && !validatePhoneNumber(phoneNumber)) {
            setPhoneNumberError('Please enter a valid phone number (e.g., 0412 345 678, 02 9123 4567, or +61 412 345 678)');
            return;
        }

        setLoading(true);

        const customerCode = sessionStorage.getItem('customerCode');
        const companyId = sessionStorage.getItem('companyId');

        const customerListStr = sessionStorage.getItem('customerList') || '[]';
        const customerList = JSON.parse(customerListStr);

        const companyData = {
            customer_code: customerCode,
            company_name: companyName.trim(),
            abn: abn.trim(),
            primary_contact: primaryContactName.trim(),
            email: email.trim(),
            phone_number: phoneNumber.trim()
        };
        console.log('Company data:', companyData);


        const idToken = sessionStorage.getItem('idToken');
        console.log('companyData:', companyData);

        try {
            let response;
            if (companyId && companyId !== 'undefined') {
                response = await fetch(`${baseURL}/webV2/visitorportal/company/${companyId}`, {
                    method: 'PATCH',
                    headers: {
                        'Authorization': idToken || '',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(companyData)
                });
            }else {
                response = await fetch(`${baseURL}/webV2/visitorportal/company`, {
                    method: 'POST',
                    headers: {
                        'Authorization': idToken || '',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(companyData)
                });
            }

            if (response.ok) {
                const result = await response.json();
                const responseData = result.returnData || result;

                console.log('Company data saved successfully:', responseData);

                // Store company_id if it exists in the response
                if (responseData.company_id) {
                    sessionStorage.setItem('companyId', responseData.company_id);
                    console.log('Stored company_id:', responseData.company_id);
                }

                showAlert('Company information saved successfully!', 'success');
                setTimeout(() => navigate('/dashboard'), 1500);
            } else {
                showAlert('Failed to save company information', 'error');
            }
        } catch (error) {
            console.error('Error saving company data:', error);
            showAlert('Error saving company information. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleGoBack = () => {
        navigate(backButtonDestination);
    };

    if (loadingCompany) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                <p>Loading company information...</p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="profile-form">
            <div className="form-group">
                <label htmlFor="companyName">
                    Company Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                    type="text"
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Enter company name"
                    required
                />
            </div>

            <div className="form-group">
                <label htmlFor="abn">
                    ABN (Australian Business Number)
                </label>
                <input
                    type="text"
                    id="abn"
                    value={abn}
                    onChange={(e) => setAbn(e.target.value)}
                    placeholder="12 345 678 901"
                    maxLength={14}
                />
                <small style={{ color: '#666', fontSize: '0.85em' }}>
                    11 digits (spaces optional)
                </small>
            </div>

            <div className="form-group">
                <label htmlFor="primaryContactName">
                    Primary Contact Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                    type="text"
                    id="primaryContactName"
                    value={primaryContactName}
                    onChange={(e) => setPrimaryContactName(e.target.value)}
                    placeholder="Enter primary contact name"
                    required
                />
            </div>

            <div className="form-group">
                <label htmlFor="email">
                    Email <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contact@company.com"
                    required
                />
            </div>

            <div className="form-group">
                <label htmlFor="phoneNumber">
                    Phone Number
                </label>
                <input
                    type="tel"
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={(e) => {
                        setPhoneNumber(e.target.value);
                        if (phoneNumberError) setPhoneNumberError('');
                    }}
                    placeholder="0412 345 678"
                    style={phoneNumberError ? { borderColor: '#f44336' } : {}}
                />
                {phoneNumberError && (
                    <small style={{ color: '#f44336', fontSize: '0.85em', marginTop: '5px', display: 'block' }}>
                        {phoneNumberError}
                    </small>
                )}
                {!phoneNumberError && (
                    <small style={{ color: '#666', fontSize: '0.85em', marginTop: '5px', display: 'block' }}>
                        Format: 0412 345 678, 02 9123 4567, or +61 412 345 678 (optional)
                    </small>
                )}
            </div>

            <div className="button-group">
                <Button type="submit" variant="primary" loading={loading}>
                    Save Changes
                </Button>
                {showGoBackButton && (
                <Button type="button" variant="secondary" onClick={handleGoBack}>
                    {backButtonLabel}
                </Button>
                )}
            </div>
        </form>
    );
};

export default CompanyForm;

